// Tiny YAML subset parser, just enough to load packages/material-db files
// without pulling in `js-yaml`. Supports:
//   - top-level mappings keyed by `id:`
//   - lists of mappings, including inline `{ key: value, ... }` form
//   - scalars: numbers, booleans, quoted/unquoted strings, null
//   - inline arrays `[a, b, c]`
//
// It is intentionally narrow. The Rust `chem-core` loader is the source of
// truth; this exists so the React UI can populate its palette before the
// wasm bundle is loaded.

import type { ChemDoc, MaterialDef } from "./types";

type Scalar = string | number | boolean | null;
type Value = Scalar | Value[] | { [key: string]: Value };

function parseScalar(raw: string): Scalar {
  const t = raw.trim();
  if (t === "" || t === "null" || t === "~") return null;
  if (t === "true") return true;
  if (t === "false") return false;
  if ((t.startsWith('"') && t.endsWith('"')) || (t.startsWith("'") && t.endsWith("'"))) {
    return t.slice(1, -1);
  }
  if (/^-?\d+(\.\d+)?([eE][-+]?\d+)?$/.test(t)) return Number(t);
  return t;
}

function parseInline(raw: string): Value {
  const t = raw.trim();
  if (t.startsWith("[") && t.endsWith("]")) {
    const body = t.slice(1, -1).trim();
    if (body === "") return [];
    return splitTopLevel(body, ",").map(parseInline);
  }
  if (t.startsWith("{") && t.endsWith("}")) {
    const body = t.slice(1, -1).trim();
    const obj: Record<string, Value> = {};
    if (body === "") return obj;
    for (const piece of splitTopLevel(body, ",")) {
      const colon = piece.indexOf(":");
      if (colon < 0) continue;
      const key = piece.slice(0, colon).trim();
      const value = piece.slice(colon + 1).trim();
      obj[key] = parseInline(value);
    }
    return obj;
  }
  return parseScalar(t);
}

function splitTopLevel(s: string, sep: string): string[] {
  const parts: string[] = [];
  let depth = 0;
  let buf = "";
  let quote: string | null = null;
  for (const ch of s) {
    if (quote) {
      buf += ch;
      if (ch === quote) quote = null;
      continue;
    }
    if (ch === '"' || ch === "'") {
      quote = ch;
      buf += ch;
      continue;
    }
    if (ch === "[" || ch === "{") depth++;
    if (ch === "]" || ch === "}") depth--;
    if (ch === sep && depth === 0) {
      parts.push(buf);
      buf = "";
      continue;
    }
    buf += ch;
  }
  if (buf.length > 0) parts.push(buf);
  return parts;
}

interface Line {
  indent: number;
  text: string;
}

function tokenize(src: string): Line[] {
  const out: Line[] = [];
  for (const raw of src.split("\n")) {
    const stripped = raw.replace(/\s+#.*$/, "").replace(/^\s*#.*$/, "");
    if (stripped.trim().length === 0) continue;
    const indent = stripped.length - stripped.trimStart().length;
    out.push({ indent, text: stripped.trim() });
  }
  return out;
}

class Cursor {
  i = 0;
  constructor(public readonly lines: Line[]) {}
  peek(): Line | null {
    return this.i < this.lines.length ? this.lines[this.i] : null;
  }
  next(): Line | null {
    return this.i < this.lines.length ? this.lines[this.i++] : null;
  }
}

function parseBlock(c: Cursor, baseIndent: number): Value {
  const first = c.peek();
  if (!first || first.indent < baseIndent) return null;
  if (first.text.startsWith("- ") || first.text === "-") {
    return parseList(c, baseIndent);
  }
  return parseMapping(c, baseIndent);
}

function parseMapping(c: Cursor, baseIndent: number): Record<string, Value> {
  const obj: Record<string, Value> = {};
  while (true) {
    const line = c.peek();
    if (!line || line.indent < baseIndent) break;
    if (line.indent > baseIndent) break;
    if (line.text.startsWith("- ")) break;
    c.next();
    const colon = line.text.indexOf(":");
    if (colon < 0) continue;
    const key = line.text.slice(0, colon).trim();
    const rest = line.text.slice(colon + 1).trim();
    if (rest.length > 0) {
      obj[key] = parseInline(rest);
    } else {
      const next = c.peek();
      if (next && next.indent > baseIndent) {
        obj[key] = parseBlock(c, next.indent);
      } else if (
        next &&
        next.indent === baseIndent &&
        (next.text.startsWith("- ") || next.text === "-")
      ) {
        // YAML's "compact" list form: a list at the *same* indent as
        // its key, e.g.
        //   cells:
        //   - { dx: 0, dy: 0, material: wood }
        // Without this branch the loader silently saw `cells: null`
        // and dropped the whole stamp — every shape emitted by the
        // `emit_grid_as_shape` helper (ram_1x8 and the gate-RAM
        // family, including the new vertical RAMs) uses this layout.
        obj[key] = parseList(c, baseIndent);
      } else {
        obj[key] = null;
      }
    }
  }
  return obj;
}

function parseList(c: Cursor, baseIndent: number): Value[] {
  const out: Value[] = [];
  while (true) {
    const line = c.peek();
    if (!line || line.indent < baseIndent) break;
    if (line.indent > baseIndent) break;
    if (!line.text.startsWith("- ") && line.text !== "-") break;
    c.next();
    const itemBody = line.text === "-" ? "" : line.text.slice(2).trim();
    if (itemBody.startsWith("{")) {
      out.push(parseInline(itemBody));
      continue;
    }
    if (itemBody.includes(":")) {
      // Inline first key/value of a mapping; remaining keys live indented.
      const colon = itemBody.indexOf(":");
      const k = itemBody.slice(0, colon).trim();
      const v = itemBody.slice(colon + 1).trim();
      const obj: Record<string, Value> = {};
      obj[k] = v.length > 0 ? parseInline(v) : null;
      const next = c.peek();
      if (next && next.indent > baseIndent) {
        const nested = parseMapping(c, next.indent);
        Object.assign(obj, nested);
      }
      out.push(obj);
      continue;
    }
    if (itemBody.length === 0) {
      const next = c.peek();
      if (next && next.indent > baseIndent) {
        out.push(parseBlock(c, next.indent));
      } else {
        out.push(null);
      }
      continue;
    }
    out.push(parseInline(itemBody));
  }
  return out;
}

export function parseYaml(src: string): Value {
  const cursor = new Cursor(tokenize(src));
  const first = cursor.peek();
  if (!first) return {};
  return parseBlock(cursor, first.indent);
}

export function parseChemDoc(src: string): ChemDoc {
  const raw = parseYaml(src) as Record<string, Value>;
  const materials = ((raw.materials ?? []) as Value[]).map(
    (m) => m as unknown as MaterialDef,
  );
  return {
    elements: (raw.elements as Value[]) ?? [],
    materials,
    reactions: (raw.reactions as Value[]) ?? [],
  };
}
